/**
 * Agrupar miles de puntos en unos pocos que un mapa pueda dibujar.
 *
 * POR QUÉ EXISTE. El filtro del paso 2 devuelve hasta 13.897 fichas, y cada una
 * dibujada como vector es un elemento en el DOM: el navegador se arrastra
 * mucho antes de llegar a mil. Pero mostrar solo las seis de la página tampoco
 * es una opción — un punto que no se dibuja es un comparable que no se
 * consideró, y seis de 13.897 no resumen el mercado, mienten sobre su forma.
 *
 * POR QUÉ UNA GRILLA Y NO UNA BIBLIOTECA. `supercluster` hace esto mejor
 * (jerárquico, con árbol espacial) y son 15 kB más una dependencia que en este
 * repo hay que instalar dentro del contenedor. Para lo que la pantalla
 * necesita —agrupar lo que se pisa en pantalla, a un zoom dado— la grilla da el
 * mismo resultado visible en treinta líneas que se pueden leer y probar.
 *
 * POR QUÉ EN PÍXELES Y NO EN GRADOS. Lo que hay que evitar es que dos puntos se
 * TAPEN, y taparse es un hecho de la pantalla, no del territorio: un grado de
 * longitud mide distinto en Arica que en Punta Arenas, y el mismo mapa alejado
 * o acercado tapa cosas distintas. Agrupando en grados, el mismo clúster
 * aparece y desaparece sin relación con lo que se ve.
 *
 * LO QUE LA GRILLA NO HACE. Las celdas empiezan en el origen de la proyección,
 * así que dos puntos muy cercanos pero a cada lado de un borde quedan en grupos
 * distintos. Es visible como un clúster de más, nunca como un punto perdido, y
 * evitarlo pide un agrupamiento iterativo que a este mapa no le compra nada.
 */

export interface PuntoUbicable {
  lat: number;
  lng: number;
}

/** Dónde cae un punto en la pantalla, a este zoom. */
export interface Pixel {
  x: number;
  y: number;
}

export interface GrupoDePuntos<T> {
  /** El CENTROIDE de los puntos, no el centro de la celda. */
  lat: number;
  lng: number;
  cantidad: number;
  /** `[[minLat, minLng], [maxLat, maxLng]]`, para poder acercarse al grupo. */
  limites: [[number, number], [number, number]];
  puntos: T[];
}

/**
 * Junta en un solo grupo los puntos que caen en la misma celda de `ladoEnPx`.
 *
 * El proyector se recibe en vez de importarlo de Leaflet: agrupar es aritmética
 * sobre una pantalla y así se puede probar sin montar un mapa en jsdom.
 *
 * Salen ORDENADOS de menor a mayor cantidad porque se dibujan en ese orden, y
 * así el grupo más numeroso queda arriba. Al revés, uno de 400 puede terminar
 * tapado por uno de 2 y el mapa esconde justo donde más mercado hay.
 */
export function agruparPorGrilla<T extends PuntoUbicable>(
  puntos: readonly T[],
  proyectar: (punto: T) => Pixel,
  ladoEnPx: number,
): GrupoDePuntos<T>[] {
  // Un lado de cero vendría de un zoom o un tamaño degenerado, y dividir por él
  // da Infinity: todas las claves colapsarían en una sola celda —o en NaN— y el
  // mapa mostraría un único clúster con todo adentro.
  const lado = ladoEnPx > 0 ? ladoEnPx : 1;

  const celdas = new Map<string, T[]>();

  for (const punto of puntos) {
    const { x, y } = proyectar(punto);
    const clave = `${Math.floor(x / lado)}:${Math.floor(y / lado)}`;
    const celda = celdas.get(clave);
    if (celda) celda.push(punto);
    else celdas.set(clave, [punto]);
  }

  const grupos: GrupoDePuntos<T>[] = [];

  for (const miembros of celdas.values()) {
    let sumaLat = 0;
    let sumaLng = 0;
    let minLat = Infinity;
    let minLng = Infinity;
    let maxLat = -Infinity;
    let maxLng = -Infinity;

    for (const { lat, lng } of miembros) {
      sumaLat += lat;
      sumaLng += lng;
      if (lat < minLat) minLat = lat;
      if (lat > maxLat) maxLat = lat;
      if (lng < minLng) minLng = lng;
      if (lng > maxLng) maxLng = lng;
    }

    grupos.push({
      lat: sumaLat / miembros.length,
      lng: sumaLng / miembros.length,
      cantidad: miembros.length,
      limites: [
        [minLat, minLng],
        [maxLat, maxLng],
      ],
      puntos: miembros,
    });
  }

  return grupos.sort((a, b) => a.cantidad - b.cantidad);
}
